import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "./components/Modal";
import RtlsMap from "./components/rtlsMap";
import { useIsMobile } from "./hooks/useIsMobile";
import {
  BuildingInProduction,
  OnlineMods,
  ActivePersonnel,
  Noncompliances,
  ModRollTime,
  WeeklyProgress,
} from "./components/modalContent";
import MOCK_MODULES from "../pages/mockData/mockModuleData";
import MOCK_PERSONNEL from "../pages/mockData/mockPersonnelData";
import "./homePage.css";
import "../animation.css";

// Derive unique property names for the "Building in Production" metric card
const uniqueProperties = [...new Set(MOCK_MODULES.map((mod) => mod.property))];

// Derive total online mod count for the "Online Mods" metric card
const onlineModCount = MOCK_MODULES.length;

// Derive active personnel count from mockPersonnelData.js
const activePersonnelCount = MOCK_PERSONNEL.length;

const HomePage = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [selectedMetric, setSelectedMetric] = useState(null);

  const modalConfig = {
    "building-in-production": {
      title: "Current Building in Production",
      component: BuildingInProduction,
      size: "large",
    },
    "online-mods": {
      title: "Online Mods",
      component: OnlineMods,
      size: "large",
    },
    "active-personnel": {
      title: "Active Personnel",
      component: ActivePersonnel,
      size: "medium",
    },
    noncompliances: {
      title: "Today's Noncompliances",
      component: Noncompliances,
      size: "xlarge",
    },
    "mod-roll-time": {
      title: "Daily Mod Roll Time",
      component: ModRollTime,
      size: "large",
    },
    "weekly-progress": {
      title: "Weekly Production Progress",
      component: WeeklyProgress,
      size: "large",
    },
  };

  const handleMetricClick = (metricId) => {
    setSelectedMetric(metricId);
  };

  const closeModal = () => {
    setSelectedMetric(null);
  };

  const currentModal = selectedMetric ? modalConfig[selectedMetric] : null;
  const ModalContent = currentModal?.component;

  return (
    <div className="home-page page-enter">
      <div className="page-header">
        <h1>RTLS Dashboard Overview</h1>
        <p className="page-subtitle">
          Real-time production monitoring and metrics
        </p>
      </div>

      {/*
        home-content is a 2-column grid on desktop (map + metrics).
        On mobile the map is omitted so the grid collapses to 1 column
        and the metrics fill the full viewport height — see homePage.css
        @media (max-width: 480px) for the layout rules.
      */}
      <div className={`home-content${isMobile ? " home-content--no-map" : ""}`}>
        {/* RTLS Map — rendered only on desktop to save resources on mobile.
            On mobile, users navigate to /rtls-map via the hamburger menu. */}
        {!isMobile && <RtlsMap isHomePage={true} />}

        {/* Metrics Grid */}
        <div className="metrics-grid">
          <div
            className="metric-card"
            onClick={() => handleMetricClick("building-in-production")}
          >
            <div className="metric-header">
              <h3>Current Building in Production</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
                <path d="M9 22v-4h6v4" />
                <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
              </svg>
            </div>
            <div className="metric-value-string">
              <div className="string-list">
                {uniqueProperties.map((property) => (
                  <span key={property} className="list-item">
                    {property}
                  </span>
                ))}
              </div>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for details</span>
            </div>
          </div>

          <div
            className="metric-card"
            onClick={() => handleMetricClick("online-mods")}
          >
            <div className="metric-header">
              <h3>Online Mods</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            </div>
            <div className="metric-value">
              <span className="value-primary">{onlineModCount}</span>
              <span className="value-label">units on line</span>
            </div>
            <div className="metric-comparison">
              <span className="comparison-label">7-day avg:</span>
              <span className="comparison-value positive">+12%</span>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for details</span>
            </div>
          </div>

          <div
            className="metric-card"
            onClick={() => handleMetricClick("active-personnel")}
          >
            <div className="metric-header">
              <h3>Active Personnel</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div className="metric-value">
              <span className="value-primary">{activePersonnelCount}</span>
              <span className="value-label">active in last 30min</span>
            </div>
            <div className="metric-comparison">
              <span className="comparison-label">7-day avg:</span>
              <span className="comparison-value neutral">-2%</span>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for details</span>
            </div>
          </div>

          <div
            className="metric-card alert"
            onClick={() => handleMetricClick("noncompliances")}
          >
            <div className="metric-header">
              <h3>Today's Noncompliances</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div className="metric-value">
              <span className="value-primary">1</span>
              <span className="value-label">failed jobs</span>
            </div>
            <div className="metric-comparison">
              <span className="comparison-label">7-day avg:</span>
              <span className="comparison-value negative">+18%</span>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for compliance dashboard</span>
            </div>
          </div>

          <div
            className="metric-card"
            onClick={() => handleMetricClick("mod-roll-time")}
          >
            <div className="metric-header">
              <h3>Daily Mod Roll Time</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="metric-value">
              <span className="value-primary">32</span>
              <span className="value-label">mins avg</span>
            </div>
            <div className="metric-comparison">
              <span className="comparison-label">7-day avg:</span>
              <span className="comparison-value positive">-8%</span>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for details</span>
            </div>
          </div>

          <div
            className="metric-card"
            onClick={() => handleMetricClick("weekly-progress")}
          >
            <div className="metric-header">
              <h3>Weekly Production Progress</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="20" x2="12" y2="10" />
                <line x1="18" y1="20" x2="18" y2="4" />
                <line x1="6" y1="20" x2="6" y2="16" />
              </svg>
            </div>
            <div className="metric-value">
              <span className="value-primary">88%</span>
              <span className="value-label">of weekly goal</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: "73%" }}></div>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for goal breakdown</span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal — close animation handled inside Modal.js via isClosing state */}
      <Modal
        isOpen={selectedMetric !== null}
        onClose={closeModal}
        title={currentModal?.title}
        size={currentModal?.size}
      >
        {ModalContent && <ModalContent />}
      </Modal>
    </div>
  );
};

export default HomePage;
